import { LitOutlinedButton } from "@/components/Button/LitButton";
import { SlotIcon } from "@/components/Icon";
import Menu, { type MenuListEntry } from "@/components/Menu";
import { sysColors } from "@/assets/theme/colors";

export interface MenuButtonProps {
  /** Items rendered inside the dropdown menu when the button is clicked. */
  actions: MenuListEntry[];
  /** Disables the button and prevents the menu from opening. */
  disabled?: boolean;
  /** Test id for the trigger button. */
  testId?: string;
  /** Trigger label. Defaults to `More` for the standard overflow affordance. */
  label?: string;
  /** Optional class override for the desktop menu panel. Detail rows widen automatically when omitted. */
  menuClassName?: string;
}

// `gap` overrides Material Web's hardcoded `:host { gap: 8px }` — there's
// no `--md-*` token for it. State-layer color/opacity match the `squared`
// Button variant so hover reads as a neutral overflow control instead of
// the brand primary tint.
const TRIGGER_STYLE: React.CSSProperties = {
  gap: "4px",
  "--md-outlined-button-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-focus-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-pressed-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-focus-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-pressed-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-state-layer-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-state-layer-opacity": "0.08",
  "--md-outlined-button-pressed-state-layer-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-pressed-state-layer-opacity": "0.12"
} as React.CSSProperties;

/**
 * An outlined button with a trailing chevron that, when clicked, opens a
 * dropdown menu of actions. Used as the overflow control in
 * {@link ActionCluster} when there are three or more actions, and usable
 * standalone whenever a header surface needs a single condensed entry point
 * for a list of related actions.
 *
 * The default label is intentionally hardcoded as "More" so the standard
 * overflow affordance reads consistently across the app. Pass `label` when a
 * surface needs a different entry point (for example, "Contact").
 */
export default function MenuButton({ actions, disabled, testId, label = "More", menuClassName }: MenuButtonProps) {
  const trigger = (
    <LitOutlinedButton disabled={disabled} data-testid={testId} style={TRIGGER_STYLE} type="button" trailingIcon>
      {label}
      <SlotIcon name="expand_more" slot="icon" disabled={disabled} size={18} />
    </LitOutlinedButton>
  );

  if (disabled) {
    return trigger;
  }

  return <Menu menuItems={actions} AnchorComponent={trigger} menuClassName={menuClassName} />;
}
